/**
 * Error Correction Exercise
 * User identifies incorrect words in a sentence and provides corrections.
 *
 * A correction of `''` means "this word should be deleted" — the single most
 * common shape in the corpus after a straight swap ("I *am* agree", "she will
 * *to* help", "*more* taller"). 28 seeded targets declare `correct: ""`, and
 * every one of them used to be unreachable: the only way to record a correction
 * was the text input, and `handleApplyCorrection` rejects empty input, so the
 * learner could flag the word but never say what was wrong with it. Deletion now
 * has its own explicit affordance rather than relying on submitting a blank
 * field, which nobody would guess.
 */

import React, { useState, useCallback } from 'react';
import { TouchableOpacity, TextInput, View } from 'react-native';
import { Box, Text, VStack, HStack } from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { colors, FS_XS, FS_SM } from '../../theme';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

/** A correction of `''` — the word should be removed rather than replaced. */
const DELETION = '';

interface ErrorInfo {
  wordIndex: number;
  incorrect: string;
  correct: string;
  acceptableCorrections?: string[];
}

interface ErrorCorrectionExerciseProps {
  prompt: string;
  sentence: string;
  errors: ErrorInfo[];
  explanation?: string;
  hint?: string;
  onComplete: (correct: boolean, score: number) => void;
}

interface CorrectionEntry {
  wordIndex: number;
  correction: string;
}

export const ErrorCorrectionExercise = React.memo(function ErrorCorrectionExercise({
  prompt,
  sentence,
  errors,
  explanation,
  hint,
  onComplete,
}: ErrorCorrectionExerciseProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();

  const words = sentence.split(/\s+/);
  const [flaggedIndex, setFlaggedIndex] = useState<number | null>(null);
  const [corrections, setCorrections] = useState<CorrectionEntry[]>([]);
  const [correctionInput, setCorrectionInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  const errorIndices = new Set(errors.map((e) => e.wordIndex));

  const isFlagged = useCallback(
    (idx: number) => corrections.some((c) => c.wordIndex === idx),
    [corrections]
  );

  const handleWordPress = useCallback(
    (idx: number) => {
      if (submitted) return;
      if (isFlagged(idx)) {
        // Unflag and remove correction
        setCorrections((prev) => prev.filter((c) => c.wordIndex !== idx));
        if (flaggedIndex === idx) {
          setFlaggedIndex(null);
          setCorrectionInput('');
        }
        return;
      }
      setFlaggedIndex(idx);
      setCorrectionInput('');
    },
    [submitted, isFlagged, flaggedIndex, setCorrectionInput]
  );

  const recordCorrection = useCallback((wordIndex: number, correction: string) => {
    setCorrections((prev) => [
      ...prev.filter((c) => c.wordIndex !== wordIndex),
      { wordIndex, correction },
    ]);
    setFlaggedIndex(null);
    setCorrectionInput('');
  }, []);

  const handleApplyCorrection = useCallback(() => {
    // Still requires text: an empty input here means "I haven't typed it yet",
    // not "delete". Deletion is the explicit button below.
    if (flaggedIndex === null || correctionInput.trim().length === 0) return;
    recordCorrection(flaggedIndex, correctionInput.trim());
  }, [flaggedIndex, correctionInput, recordCorrection]);

  const handleRemoveWord = useCallback(() => {
    if (flaggedIndex === null) return;
    recordCorrection(flaggedIndex, DELETION);
  }, [flaggedIndex, recordCorrection]);

  const handleSubmit = useCallback(() => {
    if (submitted) return;

    let correctCount = 0;
    const totalErrors = errors.length;

    for (const error of errors) {
      const userCorrection = corrections.find((c) => c.wordIndex === error.wordIndex);
      if (userCorrection) {
        const acceptable = [error.correct, ...(error.acceptableCorrections || [])].map((a) =>
          a.toLowerCase().trim()
        );
        if (acceptable.includes(userCorrection.correction.toLowerCase().trim())) {
          correctCount++;
        }
      }
    }

    const finalScore = totalErrors > 0 ? Math.round((correctCount / totalErrors) * 100) : 0;
    setScore(finalScore);
    setSubmitted(true);
    setFlaggedIndex(null);
    onComplete(finalScore === 100, finalScore);
  }, [submitted, errors, corrections, onComplete]);

  const getCorrectionFor = useCallback(
    (idx: number) => corrections.find((c) => c.wordIndex === idx),
    [corrections]
  );

  return (
    <Box
      p="$4"
      rounded="$lg"
      style={{ backgroundColor: theme.card, borderWidth: 1, borderColor: theme.border }}
    >
      <VStack space="md">
        {/* Prompt */}
        <Text
          allowFontScaling={false}
          style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: theme.text, ...ffs }}
        >
          {prompt}
        </Text>

        {hint && !submitted && (
          <Text
            allowFontScaling={false}
            style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
          >
            {hint}
          </Text>
        )}

        {/* Word chips */}
        <Box
          p="$3"
          rounded="$md"
          style={{ backgroundColor: theme.inputBg, borderWidth: 1, borderColor: theme.border }}
        >
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {words.map((word, idx) => {
              const flagged = isFlagged(idx);
              const isActive = flaggedIndex === idx;
              const isError = errorIndices.has(idx);
              const correction = getCorrectionFor(idx);

              let chipBg: string = theme.isDark ? '#374151' : '#F3F4F6';
              let chipBorder: string = theme.border;
              let textColor: string = theme.text;

              if (submitted) {
                if (isError && correction) {
                  const acceptable = [
                    errors.find((e) => e.wordIndex === idx)!.correct,
                    ...(errors.find((e) => e.wordIndex === idx)!.acceptableCorrections || []),
                  ].map((a) => a.toLowerCase().trim());
                  const isCorrectFix = acceptable.includes(correction.correction.toLowerCase().trim());
                  chipBg = isCorrectFix ? '#D1FAE5' : '#FEE2E2';
                  chipBorder = isCorrectFix ? '#059669' : '#DC2626';
                  textColor = isCorrectFix ? '#065F46' : '#991B1B';
                } else if (isError && !correction) {
                  chipBg = '#FEE2E2';
                  chipBorder = '#DC2626';
                  textColor = '#991B1B';
                }
              } else if (flagged) {
                chipBg = theme.primarySoft;
                chipBorder = theme.primary;
                textColor = theme.primary;
              } else if (isActive) {
                chipBorder = colors.primary;
              }

              return (
                <TouchableOpacity
                  key={idx}
                  testID={`error-word-${idx}`}
                  accessibilityLabel={
                    flagged
                      ? t('exercises.errorCorrection.wordFlaggedA11y', { word })
                      : t('exercises.errorCorrection.wordA11y', { word })
                  }
                  accessibilityRole="button"
                  onPress={() => handleWordPress(idx)}
                  disabled={submitted}
                  activeOpacity={0.7}
                >
                  <Box
                    px="$2"
                    py="$1"
                    rounded="$md"
                    style={{
                      backgroundColor: chipBg,
                      borderWidth: 1,
                      borderColor: chipBorder,
                    }}
                  >
                    <Text
                      allowFontScaling={false}
                      style={{
                        fontSize: FS_SM * fontScale,
                        fontWeight: '500',
                        color: textColor,
                        textDecorationLine: flagged && !submitted ? 'line-through' : 'none',
                        ...ffs,
                      }}
                    >
                      {word}
                    </Text>
                    {correction && (
                      <Text
                        allowFontScaling={false}
                        style={{
                          fontSize: FS_XS * fontScale,
                          fontWeight: '600',
                          // A deletion has no replacement text, so it is shown as
                          // a labelled strike rather than a bare "→ " with
                          // nothing after it.
                          fontStyle: correction.correction === DELETION ? 'italic' : 'normal',
                          color: submitted
                            ? textColor
                            : theme.primary,
                          ...ffs,
                        }}
                      >
                        {correction.correction === DELETION
                          ? `✕ ${t('exercises.errorCorrection.removeWordShort')}`
                          : `→ ${correction.correction}`}
                      </Text>
                    )}
                  </Box>
                </TouchableOpacity>
              );
            })}
          </View>
        </Box>

        {/* Correction input for flagged word */}
        {flaggedIndex !== null && !submitted && (
          <VStack space="xs">
            <Text
              allowFontScaling={false}
              style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
            >
              {t('exercises.errorCorrection.correctionFor', { word: words[flaggedIndex] })}
            </Text>
            <HStack space="sm" alignItems="center">
              <TextInput
                testID="error-correction-input"
                accessibilityLabel={`Correction for ${words[flaggedIndex]}`}
                accessibilityRole="text"
                value={correctionInput}
                onChangeText={setCorrectionInput}
                placeholder={t('exercises.errorCorrection.typeCorrection')}
                placeholderTextColor={theme.textSecondary}
                autoFocus
                returnKeyType="done"
                onSubmitEditing={handleApplyCorrection}
                style={{
                  flex: 1,
                  backgroundColor: theme.inputBg,
                  borderWidth: 1,
                  borderColor: theme.border,
                  borderRadius: 8,
                  padding: 10,
                  fontSize: FS_SM * fontScale,
                  color: theme.text,
                  ...ffs,
                }}
                allowFontScaling={false}
              />
              <TouchableOpacity
                testID="error-correction-apply"
                accessibilityLabel={t('exercises.errorCorrection.apply')}
                accessibilityRole="button"
                onPress={handleApplyCorrection}
                activeOpacity={0.7}
              >
                <Box
                  px="$3"
                  py="$2"
                  rounded="$md"
                  style={{ backgroundColor: colors.primary }}
                >
                  <Text
                    allowFontScaling={false}
                    style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
                  >
                    {t('exercises.errorCorrection.apply')}
                  </Text>
                </Box>
              </TouchableOpacity>
            </HStack>

            {/* Deletion is a peer of replacement, not a special case of it: for
                "I am agree" or "she will to help" there is no word to type. */}
            <TouchableOpacity
              testID="error-correction-remove"
              accessibilityLabel={t('exercises.errorCorrection.removeWordA11y', {
                word: words[flaggedIndex],
              })}
              accessibilityRole="button"
              onPress={handleRemoveWord}
              activeOpacity={0.7}
            >
              <Box
                py="$2"
                rounded="$md"
                alignItems="center"
                style={{ backgroundColor: 'transparent', borderWidth: 1, borderColor: theme.border }}
              >
                <Text
                  allowFontScaling={false}
                  style={{
                    fontSize: FS_XS * fontScale,
                    fontWeight: '600',
                    color: theme.textSecondary,
                    ...ffs,
                  }}
                >
                  ✕ {t('exercises.errorCorrection.removeWord')}
                </Text>
              </Box>
            </TouchableOpacity>
          </VStack>
        )}

        {/* Submit button */}
        {!submitted && corrections.length > 0 && flaggedIndex === null && (
          <TouchableOpacity
            testID="error-correction-submit"
            accessibilityLabel={t('exercises.errorCorrection.checkCorrections')}
            accessibilityRole="button"
            onPress={handleSubmit}
            activeOpacity={0.7}
          >
            <Box
              py="$3"
              rounded="$md"
              alignItems="center"
              style={{ backgroundColor: colors.primary }}
            >
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_SM * fontScale, fontWeight: '600', color: '#FFFFFF', ...ffs }}
              >
                {t('exercises.errorCorrection.checkCorrections')}
              </Text>
            </Box>
          </TouchableOpacity>
        )}

        {/* Result */}
        {submitted && (
          <VStack space="xs">
            <Box
              p="$2"
              rounded="$md"
              style={{ backgroundColor: score === 100 ? '#D1FAE5' : '#FEE2E2' }}
            >
              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  fontWeight: '600',
                  color: score === 100 ? '#065F46' : '#991B1B',
                  ...ffs,
                }}
              >
                {score === 100
                  ? `✓ ${t('exercises.errorCorrection.allCorrect')}`
                  : `✗ ${t('exercises.errorCorrection.score', { score })}`}
              </Text>
            </Box>

            {/* Show correct answers for missed errors */}
            {score < 100 && (
              <VStack space="xs">
                {errors.map((error, idx) => {
                  const userCorrection = corrections.find((c) => c.wordIndex === error.wordIndex);
                  const acceptable = [error.correct, ...(error.acceptableCorrections || [])].map((a) =>
                    a.toLowerCase().trim()
                  );
                  const wasFixed = userCorrection && acceptable.includes(userCorrection.correction.toLowerCase().trim());
                  if (wasFixed) return null;
                  return (
                    <Text
                      key={idx}
                      allowFontScaling={false}
                      style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}
                    >
                      {error.correct === DELETION
                        ? `"${error.incorrect}" ✕ ${t('exercises.errorCorrection.removeWordShort')}`
                        : `"${error.incorrect}" → "${error.correct}"`}
                    </Text>
                  );
                })}
              </VStack>
            )}

            {explanation && (
              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, fontStyle: 'italic', ...ffs }}
              >
                {explanation}
              </Text>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
});
